// Your notifications (/notifications): who remixed, liked, commented on your
// seeds, who followed you — Instagram's activity list, with likes of one seed
// folded into "alice and 3 others liked …". Opening the page reads them all.

import { useEffect, useMemo } from "react";
import { Link } from "react-router";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { GitBranch, Heart, MessageCircle, Monitor, Trophy, UserPlus } from "lucide-react";
import type { ApiNotification, NotificationsPage as Page } from "@shared/api-types.js";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { json, webApi } from "@web-shared/lib/web-api";
import { relativeTime } from "@web-shared/lib/format";
import { profilePath, seedPath } from "@/lib/ctas";
import { cn } from "@web-shared/lib/utils";
import { useSession } from "../session";
import { Avatar } from "../components/Avatar";
import { EmptyState, LoadMore } from "../components/CardList";
import { badgeLabel } from "../badges";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

/** The bell's number, polled while signed in. */
export function useUnreadCount(): number {
  const session = useSession();
  const unread = useQuery({
    queryKey: ["web", "unread"],
    enabled: Boolean(session),
    refetchInterval: 60_000,
    staleTime: 30_000,
    queryFn: () => webApi<{ unread: number }>("/notifications/unread-count"),
  });
  return session ? (unread.data?.unread ?? 0) : 0;
}

/** Consecutive likes of one seed become one row. */
export interface NotificationGroup {
  key: string;
  first: ApiNotification;
  others: number;
  read: boolean;
}

export function groupNotifications(items: ApiNotification[]): NotificationGroup[] {
  const out: NotificationGroup[] = [];
  for (const n of items) {
    const last = out[out.length - 1];
    if (last && n.kind === "like" && last.first.kind === "like" && last.first.seed?.id === n.seed?.id) {
      last.others += 1;
      last.read &&= n.read;
      continue;
    }
    out.push({ key: n.id, first: n, others: 0, read: n.read });
  }
  return out;
}

export function NotificationsPage() {
  const queryClient = useQueryClient();
  useDocumentTitle("Activity");
  const list = useInfiniteQuery({
    queryKey: ["web", "notifications"],
    initialPageParam: "",
    queryFn: ({ pageParam }) => webApi<Page>(pageParam ? `/notifications?cursor=${encodeURIComponent(pageParam)}` : "/notifications"),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const groups = useMemo(() => groupNotifications(items), [items]);
  const anyUnread = items.some((n) => !n.read);

  // Seen is read: once the list is on screen, clear the bell (the rows keep
  // their unread dot until the next visit).
  useEffect(() => {
    if (!anyUnread) return;
    void webApi("/notifications/read", json("POST")).then(() => queryClient.setQueryData(["web", "unread"], { unread: 0 }));
  }, [anyUnread, queryClient]);

  return (
    <div className="space-y-3">
      <h1 className="text-3xl font-black tracking-tight">Activity</h1>
      {list.isPending ? (
        <Skeleton className="h-40 w-full rounded-2xl" />
      ) : list.isError ? (
        <EmptyState>
          <p className="text-removed">Could not load your notifications.</p>
        </EmptyState>
      ) : groups.length === 0 ? (
        <EmptyState>
          <p>Nothing yet. When someone remixes, likes or comments on your seeds — or follows you — it shows up here.</p>
        </EmptyState>
      ) : (
        <>
          <ul className="divide-y divide-hairline overflow-hidden rounded-2xl border border-hairline bg-raised">
            {groups.map((g) => (
              <NotificationRow key={g.key} group={g} />
            ))}
          </ul>
          <LoadMore hasMore={list.hasNextPage} loading={list.isFetchingNextPage} onMore={() => void list.fetchNextPage()} />
        </>
      )}
    </div>
  );
}

const ICONS: Record<string, React.ReactNode> = {
  remix: <GitBranch className="size-3" />,
  like: <Heart className="size-3 fill-current" />,
  comment: <MessageCircle className="size-3" />,
  follow: <UserPlus className="size-3" />,
  badge: <Trophy className="size-3" />,
  send_to_live: <Monitor className="size-3" />,
};

function NotificationRow({ group }: { group: NotificationGroup }) {
  const n = group.first;
  const actor = n.actor;
  const who = actor ? (
    <Link to={profilePath(actor.handle)} className="font-medium hover:underline">
      {actor.username}
    </Link>
  ) : (
    <span className="font-medium">Someone</span>
  );
  const seed = n.seed ? (
    <Link to={seedPath(n.seed.id)} className="font-medium hover:underline">
      {n.seed.title}
    </Link>
  ) : null;
  const others = group.others > 0 ? ` and ${group.others} ${group.others === 1 ? "other" : "others"}` : "";

  let text: React.ReactNode;
  switch (n.kind) {
    case "remix":
      // `seed` is what they MADE, not what they made it from: the notification
      // carries the new take, which is the thing worth opening.
      text = <>{who} made {seed} from one of yours</>;
      break;
    case "like":
      text = <>{who}{others} liked {seed}</>;
      break;
    case "comment":
      text = (
        <>
          {who} commented on {seed}
          {n.comment && <span className="text-muted-foreground">: “{n.comment}”</span>}
        </>
      );
      break;
    case "follow":
      text = <>{who} followed you</>;
      break;
    case "badge":
      text = <>You earned <span className="font-medium">{badgeLabel(n.detail)}</span></>;
      break;
    case "send_to_live":
      text = <>You sent {seed} to Live</>;
      break;
  }

  return (
    <li className={cn("flex items-start gap-3 px-4 py-3", !group.read && "bg-primary/5")}>
      <span className="relative">
        <Avatar name={actor?.username ?? "Seedbed"} url={actor?.avatarUrl} className="size-9" />
        <span className="absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
          {ICONS[n.kind]}
        </span>
      </span>
      <div className="min-w-0 flex-1 text-sm">
        <p className="break-words">{text}</p>
        <p className="text-xs text-muted-foreground">{relativeTime(n.createdAt)}</p>
      </div>
      {!group.read && <span className="mt-2 size-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
    </li>
  );
}
